import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, ofType, createEffect } from '@ngrx/effects';
import { map, tap, switchMap, catchError } from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { of, forkJoin } from 'rxjs';
import * as AccountActions from './account.actions';

import { APIService } from '../../services/api/api-service';
import { notificationMessages } from '@care-portals/data';
@Injectable()
export class AccountEffects {
  constructor(
    private actions$: Actions,
    private notification: NzNotificationService,
    private api: APIService
  ) {}

  /* -------------------------------------------------------------------------- */
  /*                                   Account                                  */
  /* -------------------------------------------------------------------------- */
  loadAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.loadAccount),
      switchMap(() =>
        this.api.loadAccount().pipe(
          map(res => AccountActions.loadAccountSuccess({ accountData: res })),
          catchError(error => of(AccountActions.loadAccountFailure(error)))
        )
      )
    )
  );

  loadAccountSuccess$ = createEffect(
    () => this.actions$.pipe(ofType(AccountActions.loadAccountSuccess)),
    {
      dispatch: false
    }
  );

  /* -------------------------------------------------------------------------- */
  //#region                        Profile                                    */
  /* -------------------------------------------------------------------------- */

  editProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.editProfile),
      switchMap(({ req }) =>
        this.api.editAccountProfile(req).pipe(
          map(() => AccountActions.editProfileSuccess({ req })),
          catchError(error => of(AccountActions.editProfileFailure(error)))
        )
      )
    )
  );

  //#endregion profile

  /* -------------------------------------------------------------------------- */
  //#region                        Payment Methods                              */
  /* -------------------------------------------------------------------------- */

  loadPaymentMethods$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.loadPaymentMethods),
      switchMap(() =>
        this.api.getPaymentMethods().pipe(
          map(paymentMethodsList =>
            AccountActions.loadPaymentMethodsSuccess({ paymentMethodsList })
          ),
          catchError(error =>
            of(AccountActions.loadPaymentMethodsFailure(error))
          )
        )
      )
    )
  );

  loadPaymentMethodsSuccess$ = createEffect(
    () => this.actions$.pipe(ofType(AccountActions.loadPaymentMethodsSuccess)),
    { dispatch: false }
  );

  loadPaymentMethodsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountActions.loadPaymentMethodsFailure),
        tap(({ error }) =>
          this.notification.error(
            notificationMessages.Account_ErrorLoadingSavedPaymentMethod.title,
            notificationMessages.Account_ErrorLoadingSavedPaymentMethod.message
          )
        )
      ),
    { dispatch: false }
  );

  showAddPaymentMethod$ = createEffect(
    () => this.actions$.pipe(ofType(AccountActions.showAddPaymentMethod)),
    { dispatch: false }
  );

  addPaymentMethodAndAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.addPaymentMethodAndAddress),
      switchMap(({ paymentMethodId, address }) => {
        return forkJoin([
          this.api.addPaymentMethod(paymentMethodId),
          this.api.addDefaultAddress(address)
        ]).pipe(
          map(([paymentMethod, address]) =>
            AccountActions.addPaymentMethodSuccess()
          ),
          catchError(error => of(AccountActions.addPaymentMethodFailure(error)))
        );
      })
    )
  );

  addPaymentMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.addPaymentMethod),
      switchMap(({ token, zip }) =>
        this.api
          .addPaymentMethod(token, zip)
          .pipe(map(paymentMethod => AccountActions.addPaymentMethodSuccess()))
      ),
      catchError(error => of(AccountActions.addPaymentMethodFailure(error)))
    )
  );

  addPaymentMethodSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.addPaymentMethodSuccess),
      map(() => AccountActions.loadPaymentMethods())
    )
  );

  addPaymentMethodFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountActions.addPaymentMethodFailure),
        tap(({ error }) =>
          this.notification.error(
            'Error adding payment method',
            'Please try again'
          )
        )
      ),
    { dispatch: false }
  );

  removePaymentMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.removePaymentMethod),
      switchMap(({ paymentMethodId }) =>
        this.api
          .removePaymentMethod(paymentMethodId)
          .pipe(map(() => AccountActions.removePaymentMethodSuccess()))
      ),
      catchError(error => of(AccountActions.removePaymentMethodFailure(error)))
    )
  );

  removePaymentMethodSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.removePaymentMethodSuccess),
      map(() => AccountActions.loadPaymentMethods())
    )
  );

  removePaymentMethodFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountActions.removePaymentMethodFailure),
        tap(({ error }) =>
          this.notification.error(
            'Error removing payment method',
            'Please try again'
          )
        )
      ),
    { dispatch: false }
  );

  //#endregion payment methods

  /* -------------------------------------------------------------------------- */
  //#region                        Password                                     */
  /* -------------------------------------------------------------------------- */

  onChangePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.changePassword),
      switchMap(({ currentPassword, newPassword }) =>
        this.api.changePassword(currentPassword, newPassword).pipe(
          map(() => AccountActions.changePasswordSuccess()),
          catchError(error => of(AccountActions.changePasswordFailure(error)))
        )
      )
    )
  );

  onChangePasswordSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountActions.changePasswordSuccess),
        tap(() =>
          this.notification.success(
            'Success',
            'Your password has been changed successfully'
          )
        )
      ),
    { dispatch: false }
  );

  onChangePasswordFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountActions.changePasswordFailure),
        tap(({ error }) =>
          this.notification.error('Error changing password', error.message)
        )
      ),
    { dispatch: false }
  );
  //#endregion profile

  /* -------------------------------------------------------------------------- */
  //#region                        Address                                    */
  /* -------------------------------------------------------------------------- */

  editAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.editAddress),
      switchMap(({ req }) =>
        this.api.editAccountAddress(req).pipe(
          map(() => AccountActions.editAddressSuccess({ req })),
          catchError(error => of(AccountActions.editAddressFailure(error)))
        )
      )
    )
  );

  onEditAddressFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AccountActions.editAddressFailure),
        tap(({ error }) =>
          this.notification.error('Error changing address', error.message)
        )
      ),
    { dispatch: false }
  );

  //#endregion address
}
